import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { parseISO } from 'date-fns';
import { isEmpty } from 'lodash';
import { ErrorBoundary } from '@/components/common/ErrorBoundary';
import { Icon, IconSize } from '@/components/common/Icon';
import { MarkdownText } from '@/components/common/MarkdownText';
import { Tags } from '@/components/common/Tags';
import { useIntersectionObserver } from '@/components/hooks/useIntersectionObserver';
import { useIncidentsFeedSource } from '@/core/modules/MapPanelSources';
import { Formatter } from '@/core/utils/Formatter';
import { IncidentFeedItem } from '@/server/services/GoogleBigQuery/BigQueryIncidentsClient.types';
import { Colors } from '@/styles/colors';
import { useIncidentsTagColorsData } from '@/components/pages/SituationalAwareness/hooks/useIncidentsTagColorsData';
import { useUserIncidentsColorSchemes } from '@/components/pages/SituationalAwareness/hooks/useUserIncidentsColorSchemes';
import { getGsiTagColor, isGsiTag } from '@/components/pages/SituationalAwareness/shared/utils/getGsiTagColor';

import styles from './IncidentFeedItem.module.scss';

const formatter = new Formatter();

interface IncidentFeedItemProps {
  item: IncidentFeedItem;
  onIntersection?(): void;
}

export const IncidentFeedItemView: React.FC<IncidentFeedItemProps> = ({ item, onIntersection }) => {
  const ref = useRef<HTMLDivElement>(null);
  const { showIncidentDetails } = useIncidentsFeedSource();
  const { tagColorsByName } = useIncidentsTagColorsData();
  const time = formatter.date.format(parseISO(item.IncidentDateTime));
  const timeAgo = Formatter.getRelativeTimeAgo(item.IncidentDateTime);
  const { getIncidentColor } = useUserIncidentsColorSchemes();
  const entry = useIntersectionObserver(ref, { threshold: 0.25, once: true });
  const incidentColor = useMemo(() => getIncidentColor(item), [item]);

  const getTagColor = useCallback(
    (tag: string) => {
      if (isGsiTag(tag)) {
        return getGsiTagColor(tag);
      }

      // University|DePaul University -> try to find color by "University" prefix first, then fallback to full tag
      const [prefixOrTag] = tag.split('|');
      if (prefixOrTag && tagColorsByName?.[prefixOrTag]) {
        return tagColorsByName[prefixOrTag];
      }
      return tagColorsByName?.[tag];
    },
    [tagColorsByName],
  );

  const renderTagName = useCallback((tag: string) => {
    const [prefix, name] = tag.split('|');
    return (name || prefix).trim(); // if no prefix, the whole tag is the name
  }, []);

  useEffect(() => {
    if (onIntersection && entry?.isIntersecting) {
      onIntersection();
    }
  }, [entry?.isIntersecting, onIntersection]);

  const onDetailsClick = () => showIncidentDetails(item);

  return (
    <div className={styles.container} ref={ref}>
      <div className={styles.header}>
        <div className={styles.marker} style={{ background: incidentColor }} />
        <div className={styles.title}>{item.Title}</div>
      </div>
      <div className={styles.body}>
        <div className={styles.details}>
          <div className={styles.location}>
            <Icon name="LocationIcon" size={IconSize.s12} />
            <span>{item.City}</span>
          </div>
          <div className={styles.category}>{item.IncidentTypeName}</div>
        </div>
        {!isEmpty(item.IncidentDescription) && (
          <div className={styles.description}>
            <MarkdownText
              text={item.IncidentDescription}
              fallbackOnError={item.IncidentDescription}
              forceBlock={false}
            />
          </div>
        )}
        <div className={styles.bodyFooter}>
          <div className={styles.time}>
            <Icon name="CalendarIcon" size={IconSize.s12} />
            <span>
              {time} | {timeAgo}
            </span>
          </div>
          <div className={styles.link} onClick={onDetailsClick}>
            <span>View Details</span>
            <Icon name="ArrowRightIcon" fillColor={Colors.Blue} size={20} />
          </div>
        </div>
        <ErrorBoundary>
          <Tags tags={item.Tags} getTagStyle={getTagColor} renderTagName={renderTagName} />
        </ErrorBoundary>
      </div>
    </div>
  );
};
